import { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { ErrorBoundary } from "../components/error/ErrorBoundary";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { Button } from "../components/ui";
import { Icons } from "../icons";
import { ProtectedRoute } from "../features/auth/components/ProtectedRoute";
import { useDemoSeed } from "../features/wells/hooks/useDemoSeed";
import { useActiveWellFromRoute } from "../features/wells/hooks/useMarkActiveWell";
import { useAfeDemoSeed } from "../features/jobs/seedAfeDemo";
import { wellIdForBore } from "../features/wells/boreToWell";
import { useWellData } from "../features/wells/hooks/useWellData";

// Lazy load pages for code splitting
const AppLayout = lazy(() => import("../components/layout/AppLayout"));

// Auth pages
const LoginPage = lazy(() => import("../features/auth/components/LoginPage"));

// Feature pages
const Dashboard = lazy(() => import("../features/dashboard/Dashboard"));
const ReportsView = lazy(() => import("../features/reports/ReportsView"));
const RigsView = lazy(() => import("../features/rigs/RigsView"));
const RigEditorPage = lazy(() => import("../features/rigs/RigEditorPage"));
const PaProgramsScreen = lazy(
  () => import("../features/pa-platform/screens/PaProgramsScreen"),
);
// WoP — Work Plan authoring (Stage 0 envelope -> Stage 1 outline).
const WorkPlansScreen = lazy(
  () => import("../features/wop/screens/WorkPlansScreen"),
);
const WorkPlanScreen = lazy(
  () => import("../features/wop/screens/WorkPlanScreen"),
);
const PaProgramDetailScreen = lazy(
  () => import("../features/pa-platform/screens/PaProgramDetailScreen"),
);
const RegistryScreen = lazy(
  () => import("../features/equipment-registry/screens/RegistryScreen"),
);
const SettingsView = lazy(() => import("../features/settings/SettingsView"));
const FormationEditorScreen = lazy(
  () => import("../features/formations/screens/FormationEditorScreen"),
);
const SurveysScreen = lazy(
  () => import("../features/surveys/screens/SurveysScreen"),
);
const TimeLogScreen = lazy(
  () => import("../features/reports/screens/TimeLogScreen"),
);
// /cost-log now renders the single-day Cost Log PAGE (cost-log overhaul, PO 2026-08-05:
// day-edit is a page, not a modal). The bore Cost Log's row "Edit" + "Edit entries" and
// ReportEditModal's DailyReportRedirector navigate here with ?borefieldId&boreId&jobId(&reportId).
const CostLogDayScreen = lazy(
  () => import("../features/reports/screens/CostLogDayScreen"),
);
const BoreCostLogScreen = lazy(
  () => import("../features/reports/screens/BoreCostLogScreen"),
);
const WellTimeLogScreen = lazy(
  () => import("../features/timeline/screens/WellTimeLogScreen"),
);
const PersonnelLogScreen = lazy(
  () => import("../features/reports/screens/PersonnelLogScreen"),
);
const SlurryDisposalScreen = lazy(
  () => import("../features/reports/screens/SlurryDisposalScreen"),
);
const DrillingParamsScreen = lazy(
  () => import("../features/reports/screens/DrillingParamsScreen"),
);
const MudPropertiesScreen = lazy(
  () => import("../features/reports/screens/MudPropertiesScreen"),
);
const MudAdditivesScreen = lazy(
  () => import("../features/reports/screens/MudAdditivesScreen"),
);
const WorkingFluidScreen = lazy(
  () => import("../features/reports/screens/WorkingFluidScreen"),
);
const LoopPressureTestScreen = lazy(
  () => import("../features/reports/screens/LoopPressureTestScreen"),
);
const DrillingHazardsScreen = lazy(
  () => import("../features/reports/screens/DrillingHazardsScreen"),
);
const ProblemLogScreen = lazy(
  () => import("../features/reports/screens/ProblemLogScreen"),
);
const WellControlScreen = lazy(
  () => import("../features/reports/screens/WellControlScreen"),
);
const SafetyScreen = lazy(
  () => import("../features/reports/screens/SafetyScreen"),
);
const ZonalIsolationScreen = lazy(
  () => import("../features/reports/screens/ZonalIsolationScreen"),
);
const LoopRunningScreen = lazy(
  () => import("../features/reports/screens/LoopRunningScreen"),
);
const DrillingToolsScreen = lazy(
  () => import("../features/reports/screens/DrillingToolsScreen"),
);
// RigSigma (EDR drilling analytics) — restored 2026-08-26 after being unreachable
// since cutover Phase 6 removed its only entry, the legacy BoreDashboard.
const EdrAnalyticsScreen = lazy(
  () => import("../features/reports/screens/EdrAnalyticsScreen"),
);
const EdrResultsScreen = lazy(
  () => import("../features/reports/screens/EdrResultsScreen"),
);
const CasingScreen = lazy(
  () => import("../features/reports/screens/CasingScreen"),
);
const ESPReportScreen = lazy(
  () => import("../features/reports/screens/ESPReportScreen"),
);
const WellheadReportScreen = lazy(
  () => import("../features/reports/screens/WellheadReportScreen"),
);
const RodLiftReportScreen = lazy(
  () => import("../features/reports/screens/RodLiftReportScreen"),
);
const GasLiftReportScreen = lazy(
  () => import("../features/reports/screens/GasLiftReportScreen"),
);
const PCPReportScreen = lazy(
  () => import("../features/reports/screens/PCPReportScreen"),
);
const TimelineScreen = lazy(
  () => import("../features/timeline/components/TimelineScreen"),
);
const WellsPage = lazy(() => import("../features/wells/components/WellsPage"));
const WellCreationWizard = lazy(
  () => import("../features/wells/components/WellCreationWizard"),
);
const PadsListPage = lazy(
  () => import("../features/wells/components/PadsListPage"),
);
const PadCreationWizard = lazy(
  () => import("../features/wells/components/PadCreationWizard"),
);
const PadDetailPage = lazy(
  () => import("../features/wells/components/PadDetailPage"),
);
const WellDetailPage = lazy(
  () => import("../features/wells/components/WellDetailPage"),
);
const StimulationTimelineScreen = lazy(
  () => import("../features/stimulation/components/StimulationTimelineScreen"),
);
const JobsPage = lazy(() => import("../features/jobs/components/JobsPage"));
const JobDetailPage = lazy(
  () => import("../features/jobs/components/JobDetailPage"),
);
const AfeBuilderPage = lazy(
  () => import("../features/jobs/components/AfeBuilder/AfeBuilderPage"),
);
const AntiCollisionScreen = lazy(
  () => import("../features/anti-collision/screens/AntiCollisionScreen"),
);
const AfeStartPage = lazy(
  () => import("../features/jobs/components/AfeStart/AfeStartPage"),
);
const AfeProgramView = lazy(
  () => import("../features/jobs/components/AfeViewer/AfeProgramView"),
);

// Unauthorized page component
const UnauthorizedPage = () => {
  const navigate = useNavigate();
  return (
    <div className="unauthorized-page">
      <div className="unauthorized-content">
        <Icons.Shield />
        <h1>Access Denied</h1>
        <p>You don&apos;t have permission to access this page.</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    </div>
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <LoadingSpinner size="large" />
    <span>Loading...</span>
  </div>
);

// Root layout with suspense (unprotected, for use in protected wrapper)
// Seeds the demo dataset on app entry (relocated from the retired /borefields launcher). A null-rendering
// leaf mounted INSIDE RootLayout's ErrorBoundary so (a) a seed failure is contained to the app-shell
// boundary rather than escaping past it, and (b) its spine-store subscription re-renders only itself, not
// the whole shell (AppLayout/Outlet).
const DemoSeeder = () => {
  useDemoSeed();
  useAfeDemoSeed(); // hangs a demo AFE program off the seeded wells (RM-14 demo scaffolding)
  return null;
};

// Headless: marks the URL's well active on EVERY route (any way you reach a well —
// pad, timeline, job, report, schematic). Mounted once in RootLayout, inside Router.
const ActiveWellTracker = () => {
  useActiveWellFromRoute();
  return null;
};

// ── /borefields/* → canonical redirects (EPIC_BORE_TO_WELL_CUTOVER.md Phase 6) ────────────────
//
// The BIOT-k-vestigial Borefield DETAIL screens are retired here. The URLs stay alive as
// redirects rather than being deleted, because saved links, the sidebar bore-tree and
// ReportEditModal's DailyReportRedirector all still emit them — a 404 would be data loss from
// the user's side (Abs-7: archive, never destroy). They are deleted in Phase 7, once every
// emitter has been re-pointed.
//
// The id mappings are IDENTITIES established by the Phase 1 shim, not lookups:
//   Pad.id      == Borefield.id   (features/wells/migrateBorefields.ts)
//   Wellbore.id == Bore.id        (features/wells/boreToWell.ts — SHIM IDENTITY)
//   Well.id     == `well-${Bore.id}` (wellIdForBore)
// so each redirect is a pure string map with no store read and no async.
//
// `replace` on every one: the legacy URL must not stay in history, or Back bounces the user
// through the retired route again.

export const BorefieldToPadRedirect = () => {
  const { borefieldId } = useParams();
  // Query string preserved. Emitters historically attached ?tab= / ?action= here; the
  // interference one now routes to /pads/:padId/anti-collision directly, but saved links and
  // other params must still survive the redirect.
  const { search } = useLocation();
  return <Navigate to={`/pads/${borefieldId}${search}`} replace />;
};

export const BoreToWellRedirect = () => {
  const { borefieldId, boreId } = useParams();
  const { search } = useLocation();
  const { wells } = useWellData();

  // RESOLVE BY LOOKUP, NOT BY STRING TEMPLATE.
  //
  // The `Well.id === \`well-${boreId}\`` identity holds ONLY for wells minted by the legacy
  // migration (features/wells/boreToWell.ts). A WIZARD-NATIVE well gets two INDEPENDENT random
  // ids — `generatePrefixedId("well")` and `generateId()` for the wellbore
  // (useWellCreationWizard.ts:304-305) — so the template produced an id that does not exist and
  // the user landed on "Well not found". The sidebar deliberately surfaces wizard-native wells
  // (Sidebar.tsx:223-224), so this was reachable in normal use.
  //
  // Caught by the correctness reviewer, not by the tests: the original test asserted string →
  // string and never put a well in a store or rendered the destination, so 5/5 green said nothing
  // about whether the target resolves.
  const owner = wells.find((w) =>
    (w.wellbores ?? []).some((wb) => wb.id === boreId),
  );
  // Fall back to the legacy identity only when no owner is found — a pre-migration URL for a well
  // not in this store still lands where it used to rather than dead-ending.
  const wellId = owner?.id ?? wellIdForBore(boreId ?? "");

  // PRESERVE THE QUERY STRING. Emitters attach ?tab=, ?openReport=, ?action=run to these URLs
  // (Sidebar.tsx:396, :513, :705). A bare string target silently dropped them.
  return (
    <Navigate to={`/pads/${borefieldId}/wells/${wellId}${search}`} replace />
  );
};

export const BoreJobToJobRedirect = () => {
  const { jobId } = useParams();
  const { search } = useLocation();
  // Jobs are addressed globally (/jobs/:jobId) — the pad/bore prefix carried no extra
  // information, since a job id is unique on its own.
  return <Navigate to={`/jobs/${jobId}${search}`} replace />;
};

const RootLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <AppLayout>
        <DemoSeeder />
        <ActiveWellTracker />
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </AppLayout>
    </Suspense>
  </ErrorBoundary>
);

// Protected root layout - requires authentication
const ProtectedRootLayout = () => (
  <ProtectedRoute>
    <RootLayout />
  </ProtectedRoute>
);

// Route configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  // Auth routes (outside main layout)
  {
    path: "/login",
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: "/unauthorized",
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  // Main app routes (with layout, protected)
  {
    path: "/",
    element: <ProtectedRootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/pads" replace />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "borefields",
        children: [
          {
            // RETIRED (BIOT-k vestigial). The list launcher went first; Phase 6 retired the DETAIL
            // screens too, so every route in this subtree is now a redirect to its canonical twin.
            // The URLs are kept alive ONLY because saved links, the sidebar bore-tree and
            // ReportEditModal still emit them; they are deleted in Phase 7 once those are re-pointed.
            index: true,
            element: <Navigate to="/pads" replace />,
          },
          {
            path: ":borefieldId",
            element: <BorefieldToPadRedirect />,
          },
          {
            path: ":borefieldId/bore/:boreId",
            element: <BoreToWellRedirect />,
          },
          {
            path: ":borefieldId/bore/:boreId/job/:jobId",
            element: <BoreJobToJobRedirect />,
          },
        ],
      },
      {
        // Pads — V1-26 pad-first onboarding (primary entry). A Well is created UNDER a Pad;
        // pad operator + business unit cascade into each well. List at /pads; setup at
        // /pads/new; detail at /pads/:padId; edit at /pads/:padId/edit; pad-scoped well wizard at
        // /pads/:padId/wells/new (pad context cascades); per-well read-only detail at
        // /pads/:padId/wells/:wellId; per-well EDIT reuses the well wizard (Save) at
        // /pads/:padId/wells/:wellId/edit — exactly mirroring the pad detail→Edit→wizard flow.
        // localStorage demo data.
        path: "pads",
        children: [
          { index: true, element: <PadsListPage /> },
          { path: "new", element: <PadCreationWizard /> },
          { path: ":padId", element: <PadDetailPage /> },
          { path: ":padId/edit", element: <PadCreationWizard /> },
          // AFE program document (RM-14 S5) — the pad's facilities + per-well AFEs + cross-well
          // allocation, read-only. Opened from the pad dashboard.
          { path: ":padId/afe", element: <AfeProgramView /> },
          // ISCWSA anti-collision — pad-scoped, because anti-collision is between wells drilled
          // from the same pad. Replaces the retired `/borefields/:pad?tab=interference` entry
          // point whose only consumer (BorefieldDetail) was deleted in cutover Phase 6.
          { path: ":padId/anti-collision", element: <AntiCollisionScreen /> },
          { path: ":padId/wells/new", element: <WellCreationWizard /> },
          { path: ":padId/wells/:wellId", element: <WellDetailPage /> },
          {
            path: ":padId/wells/:wellId/edit",
            element: <WellCreationWizard />,
          },
        ],
      },
      {
        // Wells — V1-26. List at /wells; full-page creation at /wells/new; read-only detail at
        // /wells/:wellId (standalone — a padless well has no pad-scoped route); per-well EDIT at
        // /wells/:wellId/edit reuses the well wizard with Save (mirrors the pad edit pattern). The
        // pad-scoped /pads/:padId/wells/:wellId(/edit) routes still work too. localStorage demo data.
        path: "wells",
        children: [
          { index: true, element: <WellsPage /> },
          { path: "new", element: <WellCreationWizard /> },
          { path: ":wellId", element: <WellDetailPage /> },
          { path: ":wellId/edit", element: <WellCreationWizard /> },
        ],
      },
      {
        // Jobs — V1-26. The unified job finder (pad jobs + well jobs) + the "New Job" create flow at
        // /jobs; a single job's detail (header + AFEs + members) at /jobs/:jobId. localStorage demo.
        path: "jobs",
        children: [
          { index: true, element: <JobsPage /> },
          { path: ":jobId", element: <JobDetailPage /> },
        ],
      },
      {
        // AFEs — RM-14 S4. The line-item Builder (author an AFE's budget with live Python totals) at
        // /afes/:afeId/edit, opened from the AfeSection on a job. A budget is 1:1 with an AFE, so the
        // route is AFE-scoped, not job-scoped. localStorage demo data. (Viewer + list = later slices.)
        path: "afes",
        children: [
          // AFE creation entry (PO 2026-08-24): pick/confirm a well, choose the
          // class, then hand off to the Builder. Reached from Reports -> AFE.
          { index: true, element: <AfeStartPage /> },
          { path: ":afeId/edit", element: <AfeBuilderPage /> },
        ],
      },
      {
        // WoP — Work Plans. The authoring surface for the procedure layer.
        path: "work-plans",
        children: [
          { index: true, element: <WorkPlansScreen /> },
          { path: ":planId", element: <WorkPlanScreen /> },
        ],
      },
      {
        // P&A platform (Slice 2 shell) — orphan-well portfolio + per-well engagements.
        path: "pa-programs",
        children: [
          { index: true, element: <PaProgramsScreen /> },
          { path: ":programId", element: <PaProgramDetailScreen /> },
        ],
      },
      {
        path: "reports",
        element: <ReportsView />,
      },
      {
        // Rig registry list (the Land Drilling Rig registry landing).
        path: "rigs",
        element: <RigsView />,
      },
      {
        // Rich rig editor, page-based (FT-45 modal→page). New + edit.
        path: "rigs/new",
        element: <RigEditorPage />,
      },
      {
        path: "rigs/:rigId/edit",
        element: <RigEditorPage />,
      },
      {
        // Global equipment + rig registries (Test Equipment / Rigs sidebar groups).
        // App-level, cross-org shared databases (PO 2026-07-08). /registry/:category/:typeId.
        path: "registry",
        children: [{ path: ":category/:typeId", element: <RegistryScreen /> }],
      },
      {
        // Mud-log report editor. Page-based (not modal) per PO directive
        // 2026-06-01. ReportEditModal redirects here for mud-log report
        // type; route stays even though the sidebar test shortcut is gone.
        path: "formation-editor",
        element: <FormationEditorScreen />,
      },
      {
        // Survey Points editor — page-based (FT-45). Per-bore directional
        // survey sets; bore-scoped via ?borefieldId=&boreId=&surveyId=.
        // Replaces the old per-bore SurveyModal (BoreDashboard navigates
        // here). Trajectory math stays on the Python compute server.
        path: "surveys",
        element: <SurveysScreen />,
      },
      {
        // Time Log report — page-based (FT-45 Tier 1). Job-scoped multi-day:
        // ReportEditModal redirects here for the time-log type via
        // TimeLogRedirector. Auto-persists; no modal.
        path: "time-log",
        element: <TimeLogScreen />,
      },
      {
        // Cost Log — single-day PAGE editor (one page per day). Opened from the bore Cost Log's
        // row Edit / Edit entries + ReportEditModal's DailyReportRedirector.
        path: "cost-log",
        element: <CostLogDayScreen />,
      },
      {
        // The unified bore-level Cost Log (Cost Log refactor) — cross-job Year▸Month▸Day table +
        // cumulative chart. Row "Edit" / "Edit entries" NAVIGATE to the single-day page (/cost-log).
        path: "bore-cost-log",
        element: <BoreCostLogScreen />,
      },
      {
        // The unified WELL-level Time Log (IA Phase 2) — cross-job Year▸Month▸Day table. The office/
        // power-user read+backfill surface; the field-user swim-lane Timeline is the per-job editor.
        path: "well-time-log",
        element: <WellTimeLogScreen />,
      },
      {
        // Personnel Log — page-based (FT-45 Tier 1), same pattern as Time Log.
        path: "personnel-log",
        element: <PersonnelLogScreen />,
      },
      {
        // Slurry Disposal (reportType "solids-disposal") — page-based
        // (FT-45 Tier 1). Reads working-fluid siblings via jobReports.
        path: "solids-disposal",
        element: <SlurryDisposalScreen />,
      },
      {
        // Drilling Parameters — page-based (FT-45 Tier 1), same daily pattern.
        path: "drilling-params",
        element: <DrillingParamsScreen />,
      },
      {
        // Drilling Tools — page-based (modal→page conversion). One report per
        // bore/job; the form hosts many drillstrings/BHAs. ReportEditModal
        // redirects here for the drilling-tools type via DailyReportRedirector.
        path: "drilling-tools",
        element: <DrillingToolsScreen />,
      },
      {
        // Casing — page-based (program + tally + running ops). One report per
        // bore/job; the form hosts many casing strings. ReportEditModal
        // redirects here for the casing type via DailyReportRedirector.
        path: "casing",
        element: <CasingScreen />,
      },
      {
        // RigSigma ingest — upload an EDR CSV, pick a rig profile, run the
        // analysis. On completion it persists the run onto the job's report and
        // forwards to /edr-analytics/results.
        path: "edr-analytics",
        element: <EdrAnalyticsScreen />,
      },
      {
        // RigSigma results — the persisted analysis as one page of sections
        // (page shape PO-selected 2026-08-26). Reads the
        // report, so it opens cold and deep-links; it never re-queries the
        // compute server, whose datasets expire after 30 minutes.
        path: "edr-analytics/results",
        element: <EdrResultsScreen />,
      },
      {
        // ESP report — page-based downhole-equipment entry (Well Dashboard P3b).
        // Launched from the well's equipment card OR a job timeline (contract:
        // docs/COORD_esp_report_launch.md). Writes the esp_assembly string +
        // components to the completion spine; ?stringId= opens one read-only.
        path: "esp-report",
        element: <ESPReportScreen />,
      },
      {
        // Wellhead report — surface equipment (Well Dashboard P3c). Same report-launch
        // pattern as ESP; writes a wellhead component on a surface string.
        path: "wellhead-report",
        element: <WellheadReportScreen />,
      },
      {
        // Rod-lift (beam pump) report — rod string + downhole pump + surface unit
        // (Well Dashboard P3d). Same report-launch pattern as ESP.
        path: "rod-lift-report",
        element: <RodLiftReportScreen />,
      },
      {
        // Gas-lift report — mandrels + valves (Well Dashboard P3e). Same
        // report-launch pattern as ESP.
        path: "gas-lift-report",
        element: <GasLiftReportScreen />,
      },
      {
        // PCP (progressing-cavity pump) report — stator + rotor (Well Dashboard
        // P3f). Same report-launch pattern as ESP.
        path: "pcp-report",
        element: <PCPReportScreen />,
      },
      {
        // Time Log — Timeline (Differentiated Time Log). Continuous, job-scoped
        // swim-lane log + days-vs-depth, 3-day window. Preview/mule: reachable
        // standalone with seed data; binds via ?borefieldId=&boreId=&jobId=.
        path: "time-log-timeline",
        element: <TimelineScreen />,
      },
      {
        // Stimulation (Fracturing) Timeline — the 2nd lifecycle paradigm (EPIC
        // Phase 0). Standalone here renders the seeded demo mule; a real fracking
        // job reaches the same screen via the Time Log's pattern dispatch.
        path: "stimulation-timeline",
        element: <StimulationTimelineScreen />,
      },
      {
        // Mud Properties — page-based (FT-45 Tier 2). Split out of the legacy
        // drilling-fluid report + sub-type chooser.
        path: "mud-properties",
        element: <MudPropertiesScreen />,
      },
      {
        // Mud Additives — page-based (FT-45 Tier 2). Split out of the legacy
        // drilling-fluid report + sub-type chooser.
        path: "mud-additives",
        element: <MudAdditivesScreen />,
      },
      {
        // Fluid Sample — page-based (FT-45 Tier 3). One report per bore/job.
        path: "working-fluid",
        element: <WorkingFluidScreen />,
      },
      {
        // Loop Pressure Test — page-based (FT-45 Tier 3). Broken out of the
        // generic pressure-test report into its own type.
        path: "loop-pressure-test",
        element: <LoopPressureTestScreen />,
      },
      {
        // Drilling Hazards — page-based (HSE extraction).
        path: "drilling-hazards",
        element: <DrillingHazardsScreen />,
      },
      {
        // NPT / Lessons (problem) — page-based (HSE extraction).
        path: "problem",
        element: <ProblemLogScreen />,
      },
      {
        // Well Control — page-based (HSE extraction). Route per subtype
        // (active | passive | kill-sheet) via the :subType path param.
        path: "well-control/:subType",
        element: <WellControlScreen />,
      },
      {
        // Safety — page-based (HSE extraction). Route per subtype (observation
        // | near-miss | property-damage | spill-leak | injury) via :subType.
        path: "safety/:subType",
        element: <SafetyScreen />,
      },
      {
        // Zonal Isolation — page-based (FT-45 Tier 3). Simple form OR the
        // section-scoped Manager, depending on the bore's hole sections.
        path: "zonal-isolation",
        element: <ZonalIsolationScreen />,
      },
      {
        // Loop Running — page-based (FT-45 Tier 3). The hub; its related-report
        // launchers navigate to the sibling pages.
        path: "loop-running",
        element: <LoopRunningScreen />,
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute anyPermission={["admin:settings", "admin:users"]}>
            <SettingsView />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

// Router component for use in providers
export const AppRouter = () => <RouterProvider router={router} />;
